import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Store } from '../../../services/store';
import { ReportExportsStore } from '../../../services/report-exports.store';
import { toVoid } from '../../../utils/void';
import { addDays, localMidnight } from '../../../utils/date-only';
import { downloadBlob } from '../../../utils/download';
import { ReportsProvider } from './reports.provider';
import { ReportRange } from '../../../utils/report-range';
import { buildReportCsv, reportCsvFileName } from './models/report-csv';
import { ReportRowVm } from './models/report-row.view-model';

/** Arkusz Excel (the API-built workbook, 0106) or CSV (the browser-built file of 0105). */
export type ReportExportFormat = 'xlsx' | 'csv';

export interface ReportsState extends ReportRange {
  loading: boolean;
  rows: ReportRowVm[] | undefined;
  /** Page state, not URL state: the exporter's preference, back to Excel on the next visit. */
  format: ReportExportFormat;
}

// The real range comes from the URL on the first load (the page decides the defaults)
const INITIAL_STATE: ReportsState = {
  from: '',
  to: '',
  loading: false,
  rows: undefined,
  format: 'xlsx'
};

@Injectable({ providedIn: 'root' })
export class ReportsStore extends Store<ReportsState> {
  constructor(
    private provider: ReportsProvider,
    private exports: ReportExportsStore
  ) {
    super(INITIAL_STATE);
  }

  // The URL owns the range (0092): the page hands it over and the store mirrors it. Only the
  // first load shows Loading…, later ones keep the rows until the answer — Eksportuj waits
  load(range: ReportRange): Observable<void> {
    this.update(state => ({ ...state, ...range, loading: true }));

    // The API knows no time zone: two calendar days become [local 00:00 of od, local 00:00
    // of the day after do), so day boundaries are the browser's, which is the plant's
    return this.provider.getReports(localMidnight(range.from), localMidnight(addDays(range.to, 1)))
      .pipe(
        tap(rows => {
          this.update(state => ({
            ...state,
            loading: false,
            rows: rows.map(ReportRowVm.mapModelToViewModel)
          }));
        }),
        toVoid()
      );
  }

  setFormat(format: ReportExportFormat): void {
    this.update(state => ({ ...state, format }));
  }

  // Excel: the request runs in the root exports store and the file arrives in the shell's tray
  // (0107), so this page holds no export state and the button is free again at once. CSV:
  // exactly 0105's path, built here from the rows on screen, no request, no tray
  export(): void {
    const state = this.state;
    const range = { from: state.from, to: state.to };

    if (state.format === 'csv') {
      const exportedAt = new Date();
      const csv = buildReportCsv(state.rows ?? [], range, exportedAt);

      downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), reportCsvFileName(range, exportedAt));

      return;
    }

    this.exports.start('progress', range);
  }
}
